import {
    Checkbox,
    DefaultButton,
    MessageBar,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Stack,
    Text,
    TextField,
    Toggle,
} from '@fluentui/react';
import { ICodeExclusion, ISlidingFeePlan } from 'api/models/slidingFee.model';
import { Field } from 'components';
import useProcedure from 'hooks/store/useProcedure';
import useSlidingFee from 'hooks/store/useSlidingFee';
import { useSelector } from 'react-redux';
import { selectSelectedCodeExclusionIsNew, selectSpecificCodeExclusionIsValid } from 'state/slices/sliding-fee/sliding-fee.slice';
import { exclusionCalculationTypeOptions } from './ProgramPlan';

type Props = {
    programPlan?: ISlidingFeePlan;
    onProgramPlanPropertyChange: (key: keyof ISlidingFeePlan, value: string | number | ICodeExclusion[] | boolean) => void;
};

function CodeExclusionPanel(props: Props): JSX.Element {
    const { programPlan, onProgramPlanPropertyChange } = props;
    const { selectedCodeExclusion, toggleIsCodeExclusionPanelOpen, isCodeExclusionPanelOpen, setSelectedCodeExclusion } =
        useSlidingFee();
    const { proceduresOptionsList } = useProcedure();
    const exclusion = selectedCodeExclusion.data;
    const isNewExclusion = useSelector(selectSelectedCodeExclusionIsNew);

    const onExclusionPropertyChange = (key: keyof ICodeExclusion, value: string | number | boolean | undefined) =>
        setSelectedCodeExclusion({ ...exclusion, [key]: value });

    const _onDismiss = () => {
        setSelectedCodeExclusion(undefined);
        toggleIsCodeExclusionPanelOpen();
    };
    const wrapperStyle = {
        border: '1px solid #ccc',
        padding: '5px 5px 10px 10px',
    };

    const exclusionValidationErrors = useSelector(selectSpecificCodeExclusionIsValid);

    const disableSaveButton =
        (exclusionValidationErrors && exclusionValidationErrors.length > 0) || !exclusion?.code || !exclusion?.effectiveDate;
    const _onRenderFooterContent = () => {
        return (
            <Stack tokens={{ childrenGap: 12 }}>
                {exclusionValidationErrors?.length && (
                    <MessageBar messageBarType={MessageBarType.blocked}>
                        <ul style={{ margin: 0 }}>
                            {exclusionValidationErrors.map((error, index) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </MessageBar>
                )}
                <Stack horizontal grow tokens={{ childrenGap: 10 }}>
                    <PrimaryButton
                        text="Save Exclusion"
                        disabled={disableSaveButton}
                        onClick={() => {
                            if (exclusion) {
                                const programPlanExclusions = programPlan?.codeExclusions ?? [];
                                if (!isNewExclusion) {
                                    //update
                                    const newProgramPlanExclusions = [...programPlanExclusions];
                                    const indexOfExclusion = newProgramPlanExclusions.findIndex((ex) => exclusion?.id === ex.id);

                                    if (indexOfExclusion > -1) {
                                        newProgramPlanExclusions[indexOfExclusion] = exclusion;
                                        onProgramPlanPropertyChange('codeExclusions', newProgramPlanExclusions);
                                    }
                                } else {
                                    // add
                                    onProgramPlanPropertyChange('codeExclusions', [...programPlanExclusions, exclusion]);
                                }
                                setSelectedCodeExclusion(undefined);
                                toggleIsCodeExclusionPanelOpen();
                            }
                        }}
                    />
                    <DefaultButton
                        text="Cancel"
                        onClick={() => {
                            setSelectedCodeExclusion(undefined);
                            toggleIsCodeExclusionPanelOpen();
                        }}
                    />
                </Stack>
            </Stack>
        );
    };

    function onCheckboxChange(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) {
        if (checked === true && exclusion) {
            setSelectedCodeExclusion({
                ...exclusion,

                calculationType: undefined,
                absoluteFee: undefined,
                feePercentage: undefined,
                labFee: undefined,
                doesNotSlide: true,
            });
        } else {
            setSelectedCodeExclusion({
                ...exclusion,
                doesNotSlide: false,
            });
        }
    }

    return (
        <Panel
            isOpen={isCodeExclusionPanelOpen}
            onDismiss={_onDismiss}
            headerText={isNewExclusion ? 'Create Code Exclusion' : 'Edit Exclusion'}
            type={PanelType.custom}
            customWidth="800px"
            onRenderFooterContent={_onRenderFooterContent}
            isFooterAtBottom
        >
            <Stack tokens={{ childrenGap: 10 }} grow>
                <Stack tokens={{ childrenGap: 10 }} horizontal verticalAlign="end" grow wrap>
                    <Stack.Item grow>
                        <Field.SearchCombo
                            label="Start Code"
                            options={proceduresOptionsList}
                            selectedKey={exclusion?.code}
                            required
                            onChange={(event, option, index) => {
                                if (option) {
                                    onExclusionPropertyChange('code', option.key);
                                }
                            }}
                        />
                    </Stack.Item>
                    <Stack.Item grow>
                        <Field.Date
                            value={exclusion?.effectiveDate ?? ''}
                            label="Effective Date"
                            onChange={(event, newValue) => onExclusionPropertyChange('effectiveDate', newValue ? newValue : '')}
                        />
                    </Stack.Item>
                    <Stack.Item grow>
                        <Field.Date
                            value={exclusion?.expirationDate ?? ''}
                            label="Expiration Date"
                            onChange={(event, newValue) => onExclusionPropertyChange('expirationDate', newValue ? newValue : '')}
                        />
                    </Stack.Item>
                </Stack>
                <Stack tokens={{ childrenGap: 10 }} horizontal>
                    <Stack.Item>
                        <Field.Dropdown
                            options={exclusionCalculationTypeOptions.filter((option) => option.key !== 'flatFeeOnly')}
                            label="Calculation Type"
                            placeholder="Select"
                            selectedKey={exclusion?.calculationType ? exclusion?.calculationType : null}
                            style={{ minWidth: 215 }}
                            onChange={(event, option, index) =>
                                option && onExclusionPropertyChange('calculationType', option.key)
                            }
                            disabled={exclusion?.doesNotSlide}
                        />
                    </Stack.Item>
                    <Stack verticalAlign="end">
                        <Checkbox
                            label="Do not Slide"
                            checked={exclusion?.doesNotSlide}
                            onChange={(event, checked) => {
                                onCheckboxChange(event, checked);
                            }}
                        />
                    </Stack>
                </Stack>
                <Stack
                    style={
                        exclusion?.calculationType !== '' || exclusion?.calculationType === undefined
                            ? wrapperStyle
                            : {
                                  border: '',
                                  padding: '',
                              }
                    }
                    tokens={{ childrenGap: 5 }}
                    grow
                >
                    {exclusion?.calculationType === 'flatFeePerCode' && (
                        <Stack horizontal horizontalAlign="space-between">
                            <Stack>
                                <Field.Currency
                                    value={exclusion?.feePercentage ?? 0}
                                    prefix="$"
                                    label="One Flat Fee Per Code"
                                    autoComplete="off"
                                    onChange={(event, newValue) =>
                                        onExclusionPropertyChange('feePercentage', newValue ? newValue : '')
                                    }
                                    disabled={exclusion?.doesNotSlide}
                                />
                            </Stack>
                            <Stack verticalAlign="center">
                                <Text variant="medium"> {"Subject to plan's minimum and maximum rules"} </Text>
                            </Stack>
                        </Stack>
                    )}
                    {exclusion?.calculationType === 'percentagePerCode' && (
                        <Stack horizontal horizontalAlign="space-between">
                            <Stack>
                                <TextField
                                    value={exclusion?.feePercentage?.toString() ?? ''}
                                    prefix="%"
                                    label="Pt Percentage"
                                    autoComplete="off"
                                    onChange={(event, newValue) =>
                                        onExclusionPropertyChange('feePercentage', newValue ? parseInt(newValue) : '')
                                    }
                                    disabled={exclusion?.doesNotSlide}
                                />
                            </Stack>
                            <Stack tokens={{ childrenGap: 5 }} verticalAlign="center">
                                <Text variant="medium"> {"Subject to plan's minimum and maximum rules"} </Text>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
                <Stack style={wrapperStyle}>
                    <Stack horizontal horizontalAlign="space-between">
                        <Stack>
                            <Field.Currency
                                value={exclusion?.labFee ?? 0}
                                prefix="$"
                                label="Lab Fee Per Code"
                                autoComplete="off"
                                onChange={(event, newValue) => onExclusionPropertyChange('labFee', newValue ? newValue : '')}
                                disabled={exclusion?.doesNotSlide}
                            />
                            <Field.Currency
                                value={exclusion?.absoluteFee ?? 0}
                                prefix="$"
                                label="Absolute Fee Per Code"
                                autoComplete="off"
                                onChange={(event, newValue) => onExclusionPropertyChange('absoluteFee', newValue ? newValue : '')}
                                disabled={exclusion?.doesNotSlide}
                            />
                        </Stack>
                        <Stack tokens={{ childrenGap: 5 }} verticalAlign="center">
                            <Text variant="medium"> {"In addition to plan's minimum and maximum rules"} </Text>
                            <Text variant="medium"> {"In addition to plan's flat rate"}</Text>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack>
                    {!isNewExclusion && (
                        <Stack>
                            <Toggle
                                checked={exclusion?.isDeleted}
                                label="Deactivated"
                                inlineLabel
                                onChange={() => onExclusionPropertyChange('isDeleted', !exclusion?.isDeleted)}
                            />
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </Panel>
    );
}

export default CodeExclusionPanel;
